import { useRef, useState } from "react"
import { router } from "@inertiajs/react"
import { MessageSquare, Video } from "lucide-react"

import { PanelCard } from "./PanelCard"
import PanelEmpty from "./PanelEmpty"
import { AvatarStack } from "../Avatar"
import MessageThread from "../chat/MessageThread"
import ChatComposer from "../chat/ChatComposer"
import { Button } from "@/components/ui/button"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import useOptimisticSend from "@/hooks/useOptimisticSend"
import { launchVideoCall } from "@/lib/videoCall"
import { csrfToken } from "@/lib/orderActions"

// Chat tab — the customer's conversations on this order. History reads from the
// order's ConversationMessage records; sending posts to the conversation
// endpoint (the same one the legacy chat uses, Twilio-backed). The thread +
// bubbles + composer are the shared chat components, so the order Chat tab and
// the Messages inbox render the same conversation identically (design CHANGELOG
// Stage 5). Live push (new messages without a refresh) is owned by the
// messaging layer, not here.
//
// A managed order carries a second thread: the customer's own line to their
// TUW manager (AU-40 / UPDA-79). It is a different conversation from the team
// chat, so it gets its own Thread instance — the selector switches between the
// two rather than mixing them.
export default function ChatPanel({
  order,
  chatThread = "team",
  onSelectChatThread,
  chatUnread = 0,
  managerUnread = 0,
}) {
  const team = order.team ?? []
  const manager = order.manager
  const managerChat = order.managerChat
  const hasTeam = team.length > 0
  const hasManager = Boolean(manager && managerChat)

  // No team is assigned until the order leaves the queue, and an unmanaged
  // order has no manager, so there's no one to chat with yet.
  if (!hasTeam && !hasManager) {
    return (
      <PanelEmpty
        Icon={MessageSquare}
        title="Project chat"
        heading="Start the conversation"
        body="As soon as your team is assigned you can message them here — ask a question about your project and they'll be notified right away."
      />
    )
  }

  // The requested thread, unless it doesn't exist on this order.
  const thread = chatThread === "manager" && hasManager ? "manager" : hasTeam ? "team" : "manager"

  // Below 720 the fixed 620px card gives way to a viewport-relative height: on a
  // phone the thread should own what's left of the screen, with a floor so it
  // never collapses to a couple of bubbles in landscape.
  return (
    <PanelCard className="flex h-[620px] flex-col max-[720px]:h-[calc(100dvh_-_260px)] max-[720px]:min-h-[480px]">
      {hasTeam && hasManager && (
        <div className="border-b border-border px-5 py-2.5 max-[720px]:px-[14px]">
          <Tabs value={thread} onValueChange={(value) => onSelectChatThread?.(value)}>
            <TabsList aria-label="Conversation">
              <TabsTrigger value="team">
                Project chat
                <ThreadBadge count={chatUnread} />
              </TabsTrigger>
              <TabsTrigger value="manager">
                Your manager
                <ThreadBadge count={managerUnread} />
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      )}

      {thread === "manager" ? (
        <Thread
          key="manager"
          order={order}
          chat={managerChat}
          people={[manager]}
          title={`Your manager · ${manager.name}`}
          subtitle="Here to keep your project on track"
          placeholder="Message your manager…"
          emptyText="No messages yet. Your manager is here for anything about how your project is going."
          canStart
        />
      ) : (
        <Thread
          key="team"
          order={order}
          chat={order.chat ?? { sid: null, messages: [] }}
          people={team}
          title="Project chat"
          subtitle="Typically replies within a few hours"
          placeholder="Message your team…"
          emptyText="No messages yet. Say hello to your team or ask a question about your project."
        />
      )}
    </PanelCard>
  )
}

function ThreadBadge({ count }) {
  if (!count) return null
  return (
    <span className="ml-1 inline-flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
      {count}
    </span>
  )
}

// One conversation: header, history, composer. Keyed by thread in the panel so
// each thread owns its own optimistic-send queue.
function Thread({ order, chat, people, title, subtitle, placeholder, emptyText, canStart = false }) {
  const messages = chat.messages ?? []
  const composerRef = useRef(null)
  const [starting, setStarting] = useState(false)
  const [startError, setStartError] = useState(null)

  // ConversationMessage records → the normalized thread stream the shared
  // MessageThread renders (all message items; day/system lines come from the
  // unified inbox, not a single order's chat).
  const items = messages.map((m) => ({
    kind: "message",
    id: m.id,
    mine: m.mine,
    who: m.who,
    role: m.role,
    at: m.at,
    body: m.body,
  }))

  // Optimistic send: the sent bubble renders immediately, reconciles on the
  // order reload, and shows a per-bubble error with retry on failure.
  const { pendingItems, send, retry, editAndRemove, dismiss, sending } = useOptimisticSend({
    sid: chat.sid,
    only: ["order"],
    serverItems: items,
  })

  const handleEdit = (clientId) => {
    composerRef.current?.refill(editAndRemove(clientId))
  }

  // A manager thread exists only once someone opens it. The customer's click
  // creates it (Customer::OrdersController#manager_chat); the order reload then
  // carries the conversation and the composer takes the start block's place.
  const start = async () => {
    setStarting(true)
    setStartError(null)
    try {
      const response = await fetch(`/projects/${order.id}/manager_chat`, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
        credentials: "same-origin",
      })
      if (!response.ok) throw new Error("Unable to open the manager chat")
      router.reload({ only: ["order"], preserveScroll: true, onFinish: () => setStarting(false) })
    } catch {
      setStartError("We couldn't open the chat. Please try again.")
      setStarting(false)
    }
  }

  return (
    <>
      <div className="flex items-center gap-[11px] border-b border-border px-5 py-[15px] max-[720px]:flex-wrap max-[720px]:gap-y-[9px] max-[720px]:px-[14px] max-[720px]:py-3">
        <AvatarStack people={people} size={32} />
        <div className="flex-1 max-[720px]:min-w-0 max-[720px]:basis-[60%]">
          <h3 className="font-display text-sm font-bold">{title}</h3>
          <p className="mt-px flex items-center gap-1.5 text-[11.5px] text-ink-3">
            <span className="size-[7px] rounded-full bg-chart-5 max-[720px]:shrink-0" />
            <span className="max-[720px]:min-w-0 max-[720px]:truncate">{subtitle}</span>
          </p>
        </div>
        {chat.partnerUserId && (
          <Button
            variant="outline"
            size="sm"
            className="gap-[7px]"
            title="Video call"
            onClick={() =>
              launchVideoCall(chat.partnerUserId, {
                conversationSid: chat.sid,
                orderId: order.id,
              })
            }
          >
            <Video className="size-4" strokeWidth={1.9} />
            Video call
          </Button>
        )}
      </div>

      {!chat.sid && canStart ? (
        <div className="m-auto flex max-w-[300px] flex-col items-center gap-3 px-4 text-center">
          <MessageSquare className="size-7 text-ink-3" strokeWidth={1.6} />
          <p className="text-[12.5px] leading-[1.55] text-muted-foreground">
            You haven't talked to your manager here yet. Open the conversation and they'll be notified when you write.
          </p>
          <Button size="sm" onClick={start} disabled={starting}>
            {starting ? "Opening…" : "Start the conversation"}
          </Button>
          {startError && <p className="text-[12px] text-destructive">{startError}</p>}
        </div>
      ) : (
        <>
          <MessageThread
            className="max-[720px]:px-[14px] max-[720px]:py-4"
            items={[...items, ...pendingItems]}
            onRetry={retry}
            onEdit={handleEdit}
            onDismiss={dismiss}
            empty={
              <div className="m-auto max-w-[280px] text-center">
                <MessageSquare className="mx-auto size-7 text-ink-3" strokeWidth={1.6} />
                <p className="mt-3 text-[12.5px] leading-[1.55] text-muted-foreground">{emptyText}</p>
              </div>
            }
          />

          <ChatComposer
            ref={composerRef}
            conversationId={chat.sid}
            placeholder={placeholder}
            onSend={send}
            sending={sending}
            hint={null}
          />
        </>
      )}
    </>
  )
}
